import React, { useState, useEffect } from 'react';
import { Select, MenuItem, Typography, Card, CardContent, Grid, TextField, Button } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { Tooltip } from '@mui/material';

const WorkoutEntryForm = () => {
  const [formData, setFormData] = useState({});
  const [timers, setTimers] = useState({}); 
  const [drills, setDrills] = useState([]);
  const [weekNumber, setWeekNumber] = useState(1);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const [selectedDay, setSelectedDay] = useState(today);
  const isTodaySelected = selectedDay === today;

  const orderedDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const { user_id } = jwtDecode(token);

    fetch(`http://localhost:8000/custom_workout_steps/week/${user_id}/${weekNumber}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        console.log("Drill data from API:", data);
        data.forEach((d, i) => console.log(`Drill ${i + 1}:`, d));
        setDrills(data);
      })
      .catch(err => console.error("Failed to load drills", err));
  }, [weekNumber]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => {
        const updated = { ...prev };
        for (let drillId in updated) {
          if (updated[drillId]?.running && updated[drillId]?.startTime) {
            const elapsed = Math.floor((Date.now() - updated[drillId].startTime) / 1000);
            updated[drillId].elapsedSeconds = elapsed;
          }
        }
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const drillsByDay = drills.reduce((acc, drill) => {
    if (!acc[drill.day_name]) acc[drill.day_name] = [];
    acc[drill.day_name].push(drill);
    return acc;
  }, {});
  
  const filteredDrills = drillsByDay[selectedDay] || [];  

  const handleStart = (drillId) => {
    const start = Date.now();
    setTimers(prev => ({
      ...prev,
      [drillId]: { ...prev[drillId], startTime: start, running: true }
    }));
  };

  const handleStop = (drillId) => {
    const end = Date.now();
    const startTime = timers[drillId]?.startTime;
    if (!startTime) return;

    const elapsedSeconds = Math.floor((end - startTime) / 1000);

    setTimers(prev => ({
      ...prev,
      [drillId]: {
        ...prev[drillId],
        elapsedSeconds,
        running: false
      }
    }));

    setFormData(prev => ({
      ...prev,
      [drillId]: {
        ...prev[drillId],
        timer: elapsedSeconds
      }
    }));
  };

  const handleReset = (drillId) => {
    setTimers(prev => ({
      ...prev,
      [drillId]: {
        startTime: null,
        elapsedSeconds: 0,
        running: false
      }
    }));
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60).toString().padStart(2, '0');
    const sec = (seconds % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  };

  const handleChange = (drillId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [drillId]: {
        ...prev[drillId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = (drillId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
  
    const { user_id } = jwtDecode(token);
    const drill = drills.find(d => d.id === drillId);

    console.log("Submitting this drill object:", drill);

    const drillData = formData[drillId] || {};
  
    const payload = {
      user_id,
      drill_id: drill.master_drill_id || drill.drill_id || drill.drill_ref || 1,
      template_step_id: null,
      date_submitted: new Date().toISOString(),
      week_number: weekNumber,
      day_type: drill.day_name || '',
      makes: drillData.makes || 0,
      attempts: drillData.attempts || 0,
      notes: drillData.notes || '',
      timer: drillData.timer || 0,
      submitted_by: 'player',
      custom_goal_type: drill.goal_type || '',
      custom_goal_value: drill.goal_value || '',
      drill_name: drill.drill_name || ''
    };
    console.log("Submitting payload:", payload)
    fetch('http://localhost:8000/workout-log/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to save log');
        return res.json();
      })
      .then(data => {
        console.log('✅ Workout logged:', data);
        
        // 🔥 Remove the drill from local state after successful submission
        setDrills(prevDrills => prevDrills.filter(d => d.id !== drillId));
  
        // Optional: Remove from formData and timer too
        setFormData(prev => {
          const updated = { ...prev };
          delete updated[drillId];
          return updated;
        });
  
        setTimers(prev => {
          const updated = { ...prev };
          delete updated[drillId];
          return updated;
        });
      })
      .catch(err => {
        console.error('❌ Submit error:', err);
      });
  };



  return (
    <div style={{ border: '3px solid red', padding: '20px' }}>
      <h2>TEST: This is the Workout Entry Form</h2>

      <Select
        value={weekNumber}
        onChange={(e) => setWeekNumber(e.target.value)}
        sx={{ mb: 3 }}
      >
        {[1, 2, 3, 4, 5].map(week => (
          <MenuItem key={week} value={week}>
            Week {week}
          </MenuItem>
        ))}
      </Select>

      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {orderedDays.map(day => (
          <Button
            key={day}
            variant={selectedDay === day ? 'contained' : 'outlined'}
            onClick={() => setSelectedDay(day)}
          >
            {day}
          </Button>
        ))}
      </div>
 
      {filteredDrills.length === 0 ? (
        <Typography>No drills planned for today.</Typography>
      ) : (
        filteredDrills.map((drill) => (
          <Card key={drill.id} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6">{drill.drill_name}</Typography>
              <Typography variant="body2">Location: {drill.court_location}</Typography>
              <Typography variant="body2">Distance/Movement: {drill.movement_or_distance}</Typography>
              <Typography variant="body2">Progression: {drill.progression_level}</Typography>
              <Typography variant="body2">Goal: {drill.goal_type} {drill.goal_value}</Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Makes"
                    type="number"
                    fullWidth
                    value={formData[drill.id]?.makes || ''}
                    onChange={(e) => handleChange(drill.id, 'makes', parseInt(e.target.value))}
                    dissabled={!isTodaySelected}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Attempts"
                    type="number"
                    fullWidth
                    value={formData[drill.id]?.attempts || ''}
                    onChange={(e) => handleChange(drill.id, 'attempts', parseInt(e.target.value))}
                    diabled={!isTodaySelected}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    multiline
                    rows={2}
                    fullWidth
                    value={formData[drill.id]?.notes || ''}
                    onChange={(e) => handleChange(drill.id, 'notes', e.target.value)}
                    disabled={!isTodaySelected}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Time: {formatTime(timers[drill.id]?.elapsedSeconds || 0)}
                  </Typography>
                  {timers[drill.id]?.running ? (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      onClick={() => handleStop(drill.id)}
                      disabled={!isTodaySelected}
                    >
                      Stop Timer
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined" 
                      onClick={() => handleStart(drill.id)}
                      disabled={!isTodaySelected}
                    >
                      Start Timer
                    </Button>
                  )}
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={() => handleReset(drill.id)} 
                    disabled={!isTodaySelected}
                    sx={{ ml: 2 }}>
                    Reset Timer
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Tooltip title={!isTodaySelected ? "You can only submit drills for today" : ""}>
                    <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmit(drill.id)}
                    disabled={!isTodaySelected}
                  >
                    Submit Drill
                  </Button>
                 </span>
                 </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default WorkoutEntryForm;
